import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./config/msalConfig";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import LoadingScreen from "./components/LoadingScreen";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy loading de páginas - Solo Login y AdminLayout se cargan inmediatamente
import Login from "./pages/Login";
import AdminLayout from "./layouts/AdminLayout";

// Lazy loading de todas las páginas internas
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Solicitudes = lazy(() => import("./pages/Solicitudes"));
const FianzasAlquileres = lazy(() => import("./pages/FianzasAlquileres"));
const DetalleSolicitud = lazy(() => import("./pages/DetalleSolicitud"));
const EditarSolicitud = lazy(() => import("./pages/EditarSolicitud"));
const Directores = lazy(() => import("./pages/Directores"));
const DirectoresAgrupados = lazy(() => import("./pages/DirectoresAgrupados"));
const DetalleDirector = lazy(() => import("./pages/DetalleDirector"));
const DetalleSolicitudDirector = lazy(() => import("./pages/DetalleSolicitudDirector"));
const EditarDirector = lazy(() => import("./pages/EditarDirector"));
const Cotizadores = lazy(() => import("./pages/Cotizadores"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const Roles = lazy(() => import("./pages/Roles"));
const CacheAdmin = lazy(() => import("./pages/CacheAdmin"));
const GestionIntermediarios = lazy(() => import("./pages/GestionIntermediarios"));
const DetalleIntermediario = lazy(() => import("./pages/DetalleIntermediario"));
const LiquidacionesIntermediarios = lazy(() => import("./pages/LiquidacionesIntermediarios"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const Monitoring = lazy(() => import("./pages/Monitoring"));
const Backups = lazy(() => import("./pages/Backups"));
const Exportaciones = lazy(() => import("./pages/Exportaciones"));
const GestionObjetivos = lazy(() => import("./pages/GestionObjetivos"));
const Renovaciones = lazy(() => import("./pages/Renovaciones"));
const RenovacionesAgrupadas = lazy(() => import("./pages/RenovacionesAgrupadas"));
const ConfiguracionNotificaciones = lazy(() => import("./pages/ConfiguracionNotificaciones"));
const Oportunidades = lazy(() => import("./pages/Oportunidades"));
const PortalRenovaciones = lazy(() => import("./pages/PortalRenovaciones"));
const PortalRenovacionSolicitud = lazy(() => import("./pages/PortalRenovacionSolicitud"));
const Gastos = lazy(() => import("./pages/Gastos"));
const Proveedores = lazy(() => import("./pages/Proveedores"));
const Logs = lazy(() => import("./pages/Logs"));
const ModulesManagement = lazy(() => import("./pages/ModulesManagement"));
const Backlog = lazy(() => import("./pages/Backlog"));

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Verificando autenticación..." />;
  }

  if (!isAuthenticated) {
    // Guardar la ubicación actual para redirigir después del login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <NotificationProvider>
              <Suspense fallback={<LoadingScreen message="Cargando página..." />}>
                <Routes>
                <Route path="/login" element={<Login />} />

                {/* Rutas públicas - Portales de Renovaciones */}
                <Route path="/renovaciones/:token" element={<PortalRenovaciones />} />
                <Route path="/renovacion-solicitud/:token" element={<PortalRenovacionSolicitud />} />

                <Route path="/" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
                  <Route index element={<Dashboard />} />
                  <Route path="solicitudes" element={<Solicitudes />} />
                  <Route path="solicitudes/fianzas" element={<FianzasAlquileres />} />
                  <Route path="solicitudes/:id" element={<DetalleSolicitud />} />
                  <Route path="solicitudes/:id/editar" element={<EditarSolicitud />} />
                  <Route path="directores" element={<Directores />} />
                  <Route path="directores/agrupados" element={<DirectoresAgrupados />} />
                  <Route path="directores/solicitud/:gidSolicitud" element={<DetalleSolicitudDirector />} />
                  <Route path="directores/:gidSolicitud/editar" element={<EditarDirector />} />
                  <Route path="directores/:gid" element={<DetalleDirector />} />
                  <Route path="cotizadores" element={<Cotizadores />} />
                  <Route path="intermediarios/gestion" element={<GestionIntermediarios />} />
                  <Route path="intermediarios/detalle/:codigo" element={<DetalleIntermediario />} />
                  <Route path="intermediarios/liquidaciones" element={<LiquidacionesIntermediarios />} />
                  <Route path="usuarios" element={<Usuarios />} />
                  <Route path="roles" element={<Roles />} />
                  <Route path="cache" element={<CacheAdmin />} />
                  <Route path="auditoria" element={<AuditLog />} />
                  <Route path="monitoring" element={<Monitoring />} />
                  <Route path="backups" element={<Backups />} />
                  <Route path="exportaciones" element={
                    <ProtectedRoute module="administracion.exportar" permission="view" redirectToDashboard>
                      <Exportaciones />
                    </ProtectedRoute>
                  } />
                  <Route path="objetivos" element={
                    <ProtectedRoute module="comercial.objetivos" permission="view" redirectToDashboard>
                      <GestionObjetivos />
                    </ProtectedRoute>
                  } />
                  <Route path="renovaciones" element={<Renovaciones />} />
                  <Route path="renovaciones-agrupadas" element={<RenovacionesAgrupadas />} />
                  <Route path="configuracion-notificaciones" element={<ConfiguracionNotificaciones />} />
                  <Route path="oportunidades" element={<Oportunidades />} />
                  <Route path="egresos/gastos" element={<Gastos />} />
                  <Route path="egresos/proveedores" element={<Proveedores />} />
                  <Route path="logs" element={<Logs />} />
                  <Route path="modules-management" element={<ModulesManagement />} />
                  <Route path="backlog" element={<Backlog />} />
                </Route>

                <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Suspense>
            </NotificationProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
